'use client'

import {Link} from '@/src/shared/ui/link'

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Col, Collapse, Flex, Popover, Row} from 'antd'
import * as MenuUI from './deep-menu.styled'

import {usePathname, useSearchParams} from 'next/navigation'
import {createGlobalStyle} from 'styled-components'
import {BodySelector} from '@/src/config/settings'
import {ChevronRight, SquareMinus, SquarePlus} from 'lucide-react'
import {TooltipPlacement} from 'antd/es/tooltip'
import flatMap from 'lodash/flatMap'

interface UseMenuHoverOptions {
  hoverDelay?: number // задержка перед активацией (опционально)
  leaveDelay?: number // задержка перед деактивацией (для плавности)
  defaultMenuId?: number
}

export const useMenuHover = (options: UseMenuHoverOptions = {}) => {
  const {hoverDelay = 0, leaveDelay = 100, defaultMenuId = ''} = options

  const [menuId, setMenuId] = useState(defaultMenuId)

  const [isActive, setIsActive] = useState(true)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const contentRef = useRef<HTMLDivElement>(null)

  // Очистка таймаутов при размонтировании
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    }
  }, [])

  const handleMouseEnter = useCallback(
    (buttonElement: HTMLElement) => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
        leaveTimeoutRef.current = null
      }

      if (hoverDelay > 0) {
        hoverTimeoutRef.current = setTimeout(() => {
          setIsActive(true)
          setMenuId(parseInt(buttonElement.dataset.menuId!))
        }, hoverDelay)
      } else {
        setIsActive(true)
        setMenuId(parseInt(buttonElement.dataset.menuId!))
      }
    },
    [hoverDelay],
  )

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }

    if (leaveDelay > 0) {
      leaveTimeoutRef.current = setTimeout(() => {
        setIsActive(false)
      }, leaveDelay)
    } else {
      setIsActive(false)
    }
  }, [leaveDelay])

  return {
    contentRef,
    isActive,
    menuId,
    handleMouseEnter,
    handleMouseLeave,
  }
}

interface MenuProps {
  menu: LayoutMenuItem[]
}

const MenuComponent = ({menu}: MenuProps) => {
  const {handleMouseEnter, isActive, menuId} = useMenuHover({
    defaultMenuId: 0,
  })

  const activeSubsections = useMemo(() => {
    return flatMap(
      menu.map((section, i) => (i === menuId ? (section.children ?? []) : null)).filter(x => !!x),
    )
  }, [menu, menuId])

  return (
    <MenuUI.PopoverContent>
      <Row wrap={false} style={{height: '100%', maxHeight: 'calc(100vh - 150px)'}}>
        <Flex gap={33} style={{width: '100%'}}>
          <MenuUI.CategoryCol as={Col} flex="0 0 auto">
            <MenuUI.CategoryFlex as={Flex} gap={6} vertical>
              {menu.map((menuItem, i) => (
                <MenuUI.SectionButton
                  asChild
                  href={menuItem.url}
                  key={i}
                  onMouseEnter={e => handleMouseEnter(e.currentTarget)}
                  data-menu-id={i}
                  data-active={menuId == i}>
                  <span>{menuItem.title}</span>

                  {menuItem.children.length > 0 && <ChevronRight size={16} />}
                </MenuUI.SectionButton>
              ))}
            </MenuUI.CategoryFlex>
          </MenuUI.CategoryCol>
          {isActive && <MenuContent sections={activeSubsections} />}
        </Flex>
      </Row>
    </MenuUI.PopoverContent>
  )
}

const DeepMenuStyles = createGlobalStyle`
  ${BodySelector} .catalog-deep-menu {
    .ant-menu-item,
    .ant-menu-vertical > .ant-menu-submenu > .ant-menu-submenu-title {
      height: unset;
      line-height: 1.5;
      padding-left: 4px;
      padding-top: 6px;
      padding-bottom: 6px;
      font-size: 14px;

      span {
        coror: gray;
      }
    }
  }

  ${BodySelector} .catalog-deep-menu.ant-menu,
  ${BodySelector} .catalog-deep-menu.ant-menu-submenu {
    border: unset !important;
    border-right: unset !important;
    border-left: unset !important;
  }
`

export const DeepMenu = ({
  items,
  children,
}: {
  items: LayoutMenuItem[]
  children: (
    ref: React.RefObject<any>,
    open: boolean,
    setOpen: (open: boolean) => void,
  ) => React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const {ref, placement, updatePlacement} = useSmartPlacement()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [popoverContainer, setPopoverContainer] = useState()

  useEffect(() => {
    setOpen(false)
  }, [pathname, searchParams])

  return (
    <>
      <DeepMenuStyles />
      <Popover
        open={open}
        getPopupContainer={popoverContainer ? () => popoverContainer : undefined}
        rootClassName="header-deep-menu-popover"
        placement={placement}
        content={<MenuComponent menu={items} />}
        arrow={false}
        onOpenChange={open => {
          setOpen(open)
          if (open) {
            requestAnimationFrame(updatePlacement)
          }
        }}>
        {children(ref, open, setOpen)}
      </Popover>
      <PopoverContainer onMount={setPopoverContainer} />
    </>
  )
}

const PopoverContainer = ({onMount}: any) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (ref.current) {
      onMount(ref.current)
    }
  }, [ref.current])

  return <MenuUI.PopoverContainer ref={ref} />
}

function useSmartPlacement() {
  const ref = useRef<HTMLButtonElement | null>(null)
  const [placement, setPlacement] = useState<TooltipPlacement>('bottomLeft')

  const updatePlacement = () => {
    if (!ref.current) {
      return
    }

    const rect = ref.current.getBoundingClientRect()
    if (!rect) return

    const overflowRight = rect.right + 1240 > window.innerWidth

    if (overflowRight) {
      setPlacement('bottom') // fallback
    } else {
      setPlacement('bottomLeft')
    }
  }

  return {ref, placement, updatePlacement}
}

export const MenuContent = ({sections, deph = 1}: {sections: LayoutMenuItem[]; deph?: number}) => {
  const {handleMouseEnter, isActive, menuId} = useMenuHover({
    // hoverDelay: 300,
    // leaveDelay: 500,
    // defaultMenuId: _.first(catalogSections)?.id,
  })

  const catalogActiveSubsections = useMemo(() => {
    if (!menuId) {
      return []
    }

    return flatMap(sections.map((section, i) => (i === menuId ? section.children : [])))
  }, [sections, isActive, menuId])

  if (deph === 3) {
    return (
      <MenuUI.Col as={Col}>
        <SectionChildPopover childs={sections} />
      </MenuUI.Col>
    )
  }

  return (
    <>
      <MenuUI.Col as={Col}>
        <MenuUI.ColFlex as={Flex} flex="1 1 auto" gap={6} vertical>
          {sections.map((catalogSection, i) => (
            // <MenuUI.SectionGroup>
            //   <MenuUI.SectionGroupTitle></MenuUI.SectionGroupTitle>
            // </MenuUI.SectionGroup>

            <MenuUI.SubSectionButton
              asChild
              href={catalogSection.url}
              key={i}
              onMouseEnter={e => handleMouseEnter(e.currentTarget)}
              data-menu-id={i}
              data-active={menuId === i}>
              <span>{catalogSection.title}</span>
              {(catalogSection.children ?? []).length > 0 && <ChevronRight size={16} />}
            </MenuUI.SubSectionButton>
          ))}
        </MenuUI.ColFlex>
      </MenuUI.Col>
      {isActive && catalogActiveSubsections.length > 0 && (
        <MenuContent sections={catalogActiveSubsections} deph={deph + 1} />
      )}
    </>
    // <MenuUI.CatalogComponent>
    //   <SectionChildPopover childs={catalogSubSections} root />
    // </MenuUI.CatalogComponent>
  )
}

const SectionChildPopover = ({childs}: {childs: any[]}) => {
  return <NestedSectionCollapse sections={childs} />
}

const NestedSectionCollapse = ({sections}: any) => {
  return <SubSectionCollapse sections={sections} />
}

const SubSectionCollapse = ({sections, deth = 1}: {sections: LayoutMenuItem[]; deth?: number}) => {
  const [activeKey, setActiveKey] = useState<string | null>(null)

  return (
    <MenuUI.SectionCollapse
      as={Collapse}
      activeKey={activeKey || []}
      expandIcon={() => null}
      items={sections.map((section, i) => ({
        label: (deth === 1 && (
          <MenuUI.SectionCollapseLabel>
            <Link href={section.url}>{section.title}</Link>
            {(section.children ?? []).length > 0 && (
              <MenuUI.ExpandIcon
                as={activeKey === null ? SquarePlus : SquareMinus}
                data-active={activeKey === `${deth}-${i}`}
                size={16}
                onClick={() =>
                  activeKey === `${deth}-${i}` ? setActiveKey(null) : setActiveKey(`${deth}-${i}`)
                }
              />
            )}
          </MenuUI.SectionCollapseLabel>
        )) || (
          <MenuUI.SubSectionButton asChild key={i}>
            <Link href={section.url}>{section.title}</Link>

            {(section.children ?? []).length > 0 && (
              <MenuUI.ExpandIcon
                as={activeKey === null ? SquarePlus : SquareMinus}
                data-active={activeKey === `${deth}-${i}`}
                size={16}
                onClick={() =>
                  activeKey === `${deth}-${i}` ? setActiveKey(null) : setActiveKey(`${deth}-${i}`)
                }
              />
            )}
          </MenuUI.SubSectionButton>
        ),
        key: `${deth}-${i}`,
        children:
          section.children.length > 0 ? (
            <SubSectionCollapse sections={section.children ?? []} deth={deth + 1} />
          ) : undefined,
      }))}
    />
  )
}
